export type StockTransferDestination = {
  branch_id: string;
  branch_code: string;
  branch_name: string;
  eligible_stock_item_ids: string[];
};

export type StockTransferLine = {
  line_id: string;
  line_no: number;
  stock_item_id: string;
  item_code: string;
  canonical_name: string;
  unit_id: string;
  unit_name: string;
  dispatched_quantity: number;
  received_quantity: number | null;
  variance_quantity: number | null;
};

export type StockTransferSnapshot = {
  transfer_id: string;
  organization_id: string;
  source_branch_id: string;
  source_branch_code: string;
  source_branch_name: string;
  destination_branch_id: string;
  destination_branch_code: string;
  destination_branch_name: string;
  status: string;
  dispatch_request_key: string;
  receive_request_key: string | null;
  dispatch_movement_batch_id: string | null;
  receive_movement_batch_id: string | null;
  dispatch_note: string | null;
  receive_note: string | null;
  dispatch_actor_code: string | null;
  dispatch_node_code: string | null;
  receive_actor_code: string | null;
  receive_node_code: string | null;
  dispatched_at: string;
  received_at: string | null;
  completed_at: string | null;
  state_version: number;
  changed_at: string;
  lines: StockTransferLine[];
  direction?: "OUTGOING" | "INCOMING";
  next_action?: "RECEIVE" | "REVIEW_REQUIRED" | "NONE";
  result_status?: string;
};

export type StockTransferRuntimeProjection = {
  ok: boolean;
  contract: "GET_POS_STOCK_TRANSFER_RUNTIME_V1" | string;
  owner: string;
  organization_id: string;
  branch_id: string;
  projection_revision: string;
  mode: "FULL" | "DISABLED";
  can_send: boolean;
  can_receive: boolean;
  send_permission_code?: string;
  receive_permission_code?: string;
  eligible_destinations: StockTransferDestination[];
  items: StockTransferSnapshot[];
  execution_mode: "ONLINE_REQUIRED" | string;
  next_cursor: null;
  has_more: boolean;
  server_at: string;
};

type GatewayEnvelope<T> = { ok: boolean; data?: T; error?: string };

type GatewayResult<T> = { ok: boolean; data: T | null; error: string | null };

export class PosStockTransferGatewayClient {
  private readonly baseUrl: string;
  private readonly timeoutMs: number;

  constructor(options: { baseUrl: string; timeoutMs?: number }) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.timeoutMs = options.timeoutMs ?? 15_000;
  }

  private async post<T>(path: string, body: Record<string, unknown>): Promise<GatewayResult<T>> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), this.timeoutMs);
    try {
      const response = await fetch(`${this.baseUrl}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        credentials: "omit",
        signal: controller.signal,
      });
      const payload = (await response.json()) as GatewayEnvelope<T>;
      if (!response.ok || !payload.ok || !payload.data) {
        return { ok: false, data: null, error: payload.error ?? `GATEWAY_HTTP_${response.status}` };
      }
      return { ok: true, data: payload.data, error: null };
    } catch (error) {
      return {
        ok: false,
        data: null,
        error: error instanceof DOMException && error.name === "AbortError" ? "GATEWAY_TIMEOUT" : "GATEWAY_UNREACHABLE",
      };
    } finally {
      window.clearTimeout(timeout);
    }
  }

  async loadRuntime(input: { deviceSecret: string; sessionToken: string; limit?: number }) {
    return this.post<StockTransferRuntimeProjection>("/api/pos/runtime/local-projections", {
      device_secret: input.deviceSecret,
      session_token: input.sessionToken,
      domain: "STOCK_TRANSFER",
      cursor: null,
      limit: input.limit ?? 500,
    });
  }

  async dispatch(input: {
    deviceSecret: string;
    sessionToken: string;
    destinationBranchId: string;
    lines: Array<{ stockItemId: string; quantity: number }>;
    note: string | null;
    requestKey: string;
    clientContext?: Record<string, unknown>;
  }) {
    return this.post<StockTransferSnapshot>("/api/pos/runtime/stock-transfer/dispatch", {
      device_secret: input.deviceSecret,
      session_token: input.sessionToken,
      destination_branch_id: input.destinationBranchId,
      lines: input.lines.map((line) => ({ stock_item_id: line.stockItemId, quantity: line.quantity })),
      note: input.note,
      request_key: input.requestKey,
      client_context: input.clientContext ?? {},
    });
  }

  async receive(input: {
    deviceSecret: string;
    sessionToken: string;
    transferId: string;
    lines: Array<{ stockItemId: string; receivedQuantity: number }>;
    note: string | null;
    requestKey: string;
    clientContext?: Record<string, unknown>;
  }) {
    return this.post<StockTransferSnapshot>("/api/pos/runtime/stock-transfer/receive", {
      device_secret: input.deviceSecret,
      session_token: input.sessionToken,
      transfer_id: input.transferId,
      lines: input.lines.map((line) => ({ stock_item_id: line.stockItemId, received_quantity: line.receivedQuantity })),
      note: input.note,
      request_key: input.requestKey,
      client_context: input.clientContext ?? {},
    });
  }
}
